import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (redirect to login)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (username, password) => {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    
    const response = await axios.post(`${API_BASE_URL}/auth/token`, formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },

  googleLogin: () => {
    window.location.href = `${API_BASE_URL}/auth/google`;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('access_token');
  },
};

export const monitoringAPI = {
  get: (url, config) => api.get(`/monitoring${url}`, config),
  getWallboard: (minutes = 5) => api.get(`/monitoring/dashboard/wallboard?minutes=${minutes}`),
  getTraffic: (hours = 24, granularity = 'hour') => api.get(`/monitoring/dashboard/traffic?hours=${hours}&granularity=${granularity}`),
  getAudioQuality: (hours = 24) => api.get(`/monitoring/dashboard/audio-quality?hours=${hours}`),
  getRoutingQuality: (hours = 24) => api.get(`/monitoring/dashboard/routing-quality?hours=${hours}`),
  getFailures: (hours = 24) => api.get(`/monitoring/dashboard/failures?hours=${hours}`),
  getCallDetail: (callUuid) => api.get(`/monitoring/call/${callUuid}`),
  getFullCDR: (callUuid) => api.get(`/monitoring/cdr/${callUuid}`),
  searchCalls: (params) => api.get('/monitoring/calls/search', { params }),
  getClusterOverview: (minutes = 1440) => api.get(`/monitoring/dashboard/cluster-overview?minutes=${minutes}`),
  getAudioQualityByCluster: (hours = 24) => api.get(`/monitoring/dashboard/audio-quality-by-cluster?hours=${hours}`),
  getMediaServersQoS: (minutes = 60) => api.get(`/monitoring/dashboard/media-servers-qos?minutes=${minutes}`),
  getMediaServerWorstCalls: (switchname, minutes = 60) =>
    api.get(`/monitoring/dashboard/media-servers-qos/worst-calls`, { params: { switchname, minutes } }),
  getRoutingQualityCauses: (minutes = 60, top = 10) =>
    api.get(`/monitoring/dashboard/routing-quality-causes`, { params: { minutes, top } }),
  getRoutingQualityCauseByGateway: (cluster, cause, minutes = 60) =>
    api.get(`/monitoring/dashboard/routing-quality-causes/by-gateway`, { params: { cluster, cause, minutes } }),
  getRoutingQualityCauseCalls: (cluster, cause, minutes = 60) =>
    api.get(`/monitoring/dashboard/routing-quality-causes/calls`, { params: { cluster, cause, minutes } }),
  getAgentBehaviour: (minutes = 60) =>
    api.get(`/monitoring/dashboard/agent-behaviour`, { params: { minutes } }),
  getAgentBehaviourAgents: (cluster, disconnectReason, minutes = 60) =>
    api.get(`/monitoring/dashboard/agent-behaviour/agents`, { params: { cluster, disconnect_reason: disconnectReason, minutes } }),
  getAgentBehaviourCalls: (cluster, agentId, disconnectReason, minutes = 60) =>
    api.get(`/monitoring/dashboard/agent-behaviour/calls`, { params: { cluster, agent_id: agentId, disconnect_reason: disconnectReason, minutes } }),
  getAjaxFailures: (params) =>
    api.get('/monitoring/dashboard/ajax-failures', { params }),
};

export default api;
